/*
  How one person has arranged one table.

  Written 6 September 2026 for "The shared table". The job asks for a header
  that stays put while you scroll, columns you can freeze, reorder, resize and
  hide, and **a layout saved per person so it follows them between machines**.
  This file is the arranging; `ListGrid` is the drawing; `user_preferences` is
  where it is kept.

  **All of it is here rather than in the component** because this project has no
  way to render a component in a test, and the part that can go quietly wrong is
  not the drawing. It is what happens to somebody's saved arrangement when the
  table gains a column, loses one, or is renamed: a saved layout is a list of
  names written down months ago, and the table it describes has moved on.
*/

/* A column's name in a layout. Labels are already unique within a table, which
   is what ListGrid keys its headings by, so a column only needs `key` when its
   label is going to change. */
export function columnKey(col) {
  return col.key || col.label;
}

export function defaultLayout(columns) {
  return { order: columns.map(columnKey), hidden: [], widths: {}, frozen: 0 };
}

/*
  **Reading back a layout has to survive the table changing**, and this is the
  whole reason this file exists. Four things can be wrong with a saved layout
  and none of them is an error:

  - a column it names has been deleted: drop it
  - a column exists that it does not name: put it at the end, visible
  - it hides every column: ignore the hiding, because a table with no columns is
    a bug that looks like a person's own doing
  - it is not JSON at all, or is JSON of the wrong shape: use the default

  A person's arrangement is worth keeping and is never worth a blank screen.
*/
export function parseLayout(text, columns) {
  const base = defaultLayout(columns);
  let saved;
  try { saved = JSON.parse(text); } catch { return base; }
  if (!saved || typeof saved !== 'object' || !Array.isArray(saved.order)) return base;

  const known = new Set(base.order);
  const order = saved.order.filter(k => known.has(k));
  for (const k of base.order) if (!order.includes(k)) order.push(k);

  const hidden = (Array.isArray(saved.hidden) ? saved.hidden : []).filter(k => order.includes(k));
  const visible = order.filter(k => !hidden.includes(k));

  const widths = {};
  if (saved.widths && typeof saved.widths === 'object') {
    for (const [k, v] of Object.entries(saved.widths)) {
      const n = Number(v);
      if (order.includes(k) && Number.isFinite(n) && n >= MIN_WIDTH) widths[k] = Math.round(n);
    }
  }

  const frozen = Number.isInteger(saved.frozen)
    ? Math.max(0, Math.min(saved.frozen, visible.length))
    : 0;

  return {
    order,
    hidden: visible.length === 0 ? [] : hidden,
    widths,
    frozen,
  };
}

export function serialiseLayout(layout) {
  return JSON.stringify({
    order: layout.order, hidden: layout.hidden, widths: layout.widths, frozen: layout.frozen,
  });
}

/* A column narrower than this is a column you cannot read or grab hold of
   again, so a drag stops here rather than letting somebody lose one. */
export const MIN_WIDTH = 64;

export function setWidth(widths, key, px) {
  return { ...widths, [key]: Math.max(MIN_WIDTH, Math.round(px)) };
}

/*
  Moving a column. `to` is where it lands in the order as a whole, and it is
  clamped rather than trusted: a button pressed on the last row must not move
  anything off the end.
*/
export function moveColumn(order, key, to) {
  const from = order.indexOf(key);
  if (from < 0) return order;
  const target = Math.max(0, Math.min(to, order.length - 1));
  if (target === from) return order;
  const next = [...order];
  next.splice(from, 1);
  next.splice(target, 0, key);
  return next;
}

/*
  Hiding a column. **The last visible column cannot be hidden**: a table with no
  columns has no rows on screen either, and it would look like the report found
  nothing rather than like a column menu gone too far.
*/
export function toggleHidden(hidden, key, order) {
  if (hidden.includes(key)) return hidden.filter(k => k !== key);
  const visible = order.filter(k => !hidden.includes(k));
  if (visible.length <= 1) return hidden;
  return [...hidden, key];
}

/* The columns as this person has them: their order, their widths, and only the
   ones they have kept. */
export function arrange(columns, layout) {
  const byKey = new Map(columns.map(c => [columnKey(c), c]));
  return layout.order
    .filter(k => !layout.hidden.includes(k))
    .map(k => byKey.get(k))
    .filter(Boolean)
    .map(c => {
      const w = layout.widths[columnKey(c)];
      return w ? { ...c, width: w + 'px' } : c;
    });
}

/*
  How far from the left edge each frozen column sits, so it can be stuck there
  while the rest scrolls under it.

  **A column can only be frozen if its width is a number of pixels.** A column
  measured in `fr` has no width until the browser has laid the row out, so
  sticking it at a computed offset would put it in the wrong place. Freezing
  stops at the first column that has no pixel width rather than guessing one.
*/
export function frozenOffsets(columns, frozen) {
  const out = [];
  let x = 0;
  for (let i = 0; i < Math.min(frozen, columns.length); i += 1) {
    const px = pixelWidth(columns[i].width);
    if (px === null) break;
    out.push(x);
    x += px;
  }
  return out;
}

export function pixelWidth(width) {
  const m = /^(\d+(?:\.\d+)?)px$/.exec(String(width || '').trim());
  return m ? Number(m[1]) : null;
}
